import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CheckCheck, Plus, X } from 'lucide-react'
import { reconcileAccount, type AccountSummary, type ReconcileResult } from '@/api/accounts'
import { AmountCalculatorInput } from '@/components/AmountCalculatorInput'
import { DatePicker } from '@/components/DatePicker'
import { TransactionList } from '@/components/transactions/TransactionList'
import { errorText } from '@/components/transactions/add/formLogic'
import { Button } from '@/components/ui/button'
import { useFormatCurrency } from '@/hooks/useFormatCurrency'
import { useTransactionPages } from '@/hooks/useTransactionPages'
import { formatShortDate } from '@/lib/formatDate'
import { ALL_TIME_FROM } from '@/lib/transactionsLink'

const LABEL_CLS = 'mb-1 block text-sm font-medium text-[var(--fl-text-1)]'

/** Today in the viewer's own time zone; toISOString would give UTC's date. */
function localToday(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** The rows still to tick up to the date: where a missing or wrong one usually shows up. */
function OpenRows({ accountId, asOf }: { accountId: string; asOf: string }) {
  const pages = useTransactionPages({
    financialAccountId: accountId,
    dateFrom: ALL_TIME_FROM,
    dateTo: asOf,
    reconciled: false,
  })
  if (!pages.isLoading && !pages.isError && pages.transactions.length === 0) {
    return (
      <p className="rounded-xl border border-[var(--fl-border)] px-4 py-3 text-sm text-[var(--fl-text-2)]">
        Everything up to {formatShortDate(asOf)} is already ticked, so the difference is most likely
        something not entered in the app yet.
      </p>
    )
  }
  return <TransactionList pages={pages} hasFilters={false} compact focusAccountId={accountId} />
}

/**
 * Checks the account against the balance its bank shows on a date. Only a match reconciles it,
 * so a transaction that was never entered shows up as a difference.
 */
export function ReconcileModal({
  account,
  onAddMissing,
  onClose,
}: {
  account: AccountSummary
  onAddMissing: () => void
  onClose: () => void
}) {
  const { symbol, format } = useFormatCurrency()
  const queryClient = useQueryClient()
  // A card's balance is stored negative; the bank shows what's owed, so the field takes that.
  const owed = account.type === 'CREDIT_CARD'
  const [amount, setAmount] = useState('')
  const [asOf, setAsOf] = useState(localToday())
  const [result, setResult] = useState<ReconcileResult | null>(null)

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const shown = (n: number) => format(owed ? -n : n)
  const signed = () => (owed ? -Math.abs(Number(amount)) : Number(amount))
  const future = asOf > localToday()
  const canCheck = amount.trim() !== '' && !isNaN(Number(amount)) && !!asOf && !future

  const mutation = useMutation({
    mutationFn: (adjust: boolean) =>
      reconcileAccount(account.id, { asOf, balance: signed(), adjust }),
    onSuccess: (res) => {
      setResult(res)
      if (res.matched) {
        for (const key of ['accounts', 'transactions', 'dashboard', 'budget', 'analytics'])
          queryClient.invalidateQueries({ queryKey: [key] })
      }
    },
  })

  const differenceText = (r: ReconcileResult) => {
    // In the owed terms the person typed, so "more" on a card means more owed.
    const gap = owed ? -r.difference : r.difference
    return `Off by ${format(Math.abs(gap))}: the ${owed ? 'statement' : 'bank'} shows ${
      gap > 0 ? 'more' : 'less'
    }${owed ? ' owed' : ''}`
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="reconcile-title"
      className="fixed inset-0 z-50 overflow-y-auto bg-black/30"
    >
      <div
        className="flex min-h-full items-center justify-center"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose()
        }}
      >
        <div className="mx-4 my-8 w-full max-w-md rounded-2xl bg-[var(--fl-surface)] p-6 shadow-[0_8px_40px_rgba(0,0,0,0.12)] md:p-7">
          <div className="mb-1 flex items-start justify-between gap-3">
            <h2
              id="reconcile-title"
              className="font-serif text-[22px] font-semibold text-[var(--fl-heading)]"
            >
              Reconcile {account.name}
            </h2>
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className="-mr-2 -mt-2 grid h-11 w-11 shrink-0 place-items-center rounded-lg text-[var(--fl-text-2)] hover:bg-[var(--fl-surface-hover)]"
            >
              <X size={20} aria-hidden="true" />
            </button>
          </div>

          {result?.matched ? (
            <div className="flex flex-col gap-4">
              <p className="flex items-start gap-2 rounded-xl bg-[var(--fl-primary-subtle)] px-4 py-3 text-sm text-[var(--fl-primary-text)]">
                <CheckCheck size={18} aria-hidden="true" className="mt-px shrink-0" />
                <span>
                  {result.difference === 0
                    ? 'Balances match.'
                    : `Recorded ${format(Math.abs(result.difference))} as an adjustment.`}{' '}
                  Reconciled through {formatShortDate(result.asOf)} at {shown(result.bankBalance)}
                  {result.ticked > 0 &&
                    `; ${result.ticked} transaction${result.ticked === 1 ? '' : 's'} ticked off`}
                  .
                </span>
              </p>
              <Button className="h-11 font-semibold" onClick={onClose}>
                Done
              </Button>
            </div>
          ) : (
            <>
              <p className="mb-5 text-sm text-[var(--fl-text-3)]">
                Enter the balance your {owed ? 'card statement' : 'bank'} shows. If it matches the
                app, everything up to that day is ticked off.
              </p>
              <div className="flex flex-col gap-4">
                <div>
                  <label className={LABEL_CLS} htmlFor="reconcile-balance">
                    {owed ? `Amount owed (${symbol})` : `Balance the bank shows (${symbol})`}
                  </label>
                  <AmountCalculatorInput
                    id="reconcile-balance"
                    value={amount}
                    onChange={(v) => {
                      setAmount(v)
                      setResult(null)
                    }}
                    placeholder="0.00"
                  />
                </div>
                <div>
                  <label className={LABEL_CLS} htmlFor="reconcile-date">
                    As of
                  </label>
                  <DatePicker
                    id="reconcile-date"
                    value={asOf}
                    onChange={(v) => {
                      setAsOf(v)
                      setResult(null)
                    }}
                  />
                  <p className="mt-1 text-xs text-[var(--fl-text-3)]">
                    Today for your banking app, or the closing date on a statement.
                  </p>
                  {future && (
                    <p className="mt-1 text-xs text-[var(--fl-error)]">
                      The date can&rsquo;t be in the future.
                    </p>
                  )}
                </div>

                {result && !result.matched && (
                  <div className="flex flex-col gap-3 rounded-xl border border-[var(--fl-warning-text)] bg-[var(--fl-warning-bg)] px-4 py-3">
                    <dl className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <dt className="text-xs text-[var(--fl-text-2)]">
                          App on {formatShortDate(result.asOf)}
                        </dt>
                        <dd className="font-semibold tabular-nums text-[var(--fl-text-1)]">
                          {shown(result.appBalance)}
                        </dd>
                      </div>
                      <div>
                        <dt className="text-xs text-[var(--fl-text-2)]">
                          {owed ? 'Statement' : 'Bank'}
                        </dt>
                        <dd className="font-semibold tabular-nums text-[var(--fl-text-1)]">
                          {shown(result.bankBalance)}
                        </dd>
                      </div>
                    </dl>
                    <p className="text-sm font-medium text-[var(--fl-warning-dark)]">
                      {differenceText(result)}
                    </p>
                    <p className="text-xs text-[var(--fl-text-2)]">
                      Look for a missing or wrong transaction below, then check again.
                    </p>
                  </div>
                )}

                {mutation.isError && (
                  <p className="text-xs text-[var(--fl-error)]">{errorText(mutation.error)}</p>
                )}

                {result && !result.matched ? (
                  <div className="flex flex-col gap-2">
                    <div className="flex gap-2.5">
                      <Button
                        variant="outline"
                        className="h-11 flex-1"
                        onClick={onAddMissing}
                        disabled={mutation.isPending}
                      >
                        <Plus size={15} aria-hidden="true" /> Add missing
                      </Button>
                      <Button
                        className="h-11 flex-1 font-semibold"
                        onClick={() => mutation.mutate(false)}
                        disabled={!canCheck || mutation.isPending}
                      >
                        {mutation.isPending ? 'Checking…' : 'Check again'}
                      </Button>
                    </div>
                    <Button
                      variant="ghost"
                      className="h-10 text-[var(--fl-text-2)]"
                      onClick={() => mutation.mutate(true)}
                      disabled={!canCheck || mutation.isPending}
                    >
                      Record {format(Math.abs(result.difference))} as an adjustment and reconcile
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2.5 pt-1">
                    <Button variant="outline" className="h-11 flex-1" onClick={onClose}>
                      Cancel
                    </Button>
                    <Button
                      className="h-11 flex-1 font-semibold"
                      onClick={() => mutation.mutate(false)}
                      disabled={!canCheck || mutation.isPending}
                    >
                      {mutation.isPending ? 'Checking…' : 'Check'}
                    </Button>
                  </div>
                )}
              </div>

              {result && !result.matched && (
                <section aria-label="Not yet ticked" className="mt-5 flex flex-col gap-2">
                  <h3 className="px-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--fl-text-3)]">
                    Not yet ticked, up to {formatShortDate(result.asOf)}
                  </h3>
                  <OpenRows accountId={account.id} asOf={result.asOf} />
                </section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
